import { useEffect, useState } from 'react'
import { Check, Loader2, Trophy } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  useCreateConfiguracionFidelizacion,
  useGruposDisponiblesFidelizacion,
  useUpdateConfiguracionFidelizacion,
} from '../hooks/useFidelizacion'
import { parseFidelizacionError } from '../hooks/useFidelizacionError'
import {
  COLORES_DISPONIBLES,
  TONOS_DISPONIBLES,
  colorHex,
  normalizeColor,
} from '../lib/reporteColor'
import type {
  ConfiguracionFidelizacion,
  ConfiguracionFidelizacionCreateInput,
  ConfiguracionFidelizacionListItem,
} from '../types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  configuracion?: ConfiguracionFidelizacion | ConfiguracionFidelizacionListItem | null
}

const emptyForm: ConfiguracionFidelizacionCreateInput = {
  nombre: '',
  slug: '',
  descripcion: '',
  grupo_id: 0,
  api_base_url: '',
  api_key: '',
  activo: true,
  color_nombre: 'emerald',
  color_tono: '500',
}

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

export function FidelizacionFormDialog({ open, onOpenChange, configuracion }: Props) {
  const [form, setForm] = useState<ConfiguracionFidelizacionCreateInput>(emptyForm)
  const [keepApiKey, setKeepApiKey] = useState(true)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [slugTouched, setSlugTouched] = useState(false)

  const grupos = useGruposDisponiblesFidelizacion()
  const createMut = useCreateConfiguracionFidelizacion()
  const updateMut = useUpdateConfiguracionFidelizacion()

  const isEditing = !!configuracion

  useEffect(() => {
    if (!open) return
    setErrorMsg(null)
    setSlugTouched(false)
    if (configuracion) {
      const c = normalizeColor(configuracion)
      setForm({
        nombre: configuracion.nombre,
        slug: configuracion.slug,
        descripcion: 'descripcion' in configuracion ? configuracion.descripcion : '',
        grupo_id: configuracion.grupo_id,
        api_base_url: configuracion.api_base_url,
        api_key: '',
        activo: configuracion.activo,
        color_nombre: c.nombre,
        color_tono: c.tono,
      })
      setKeepApiKey(true)
    } else {
      setForm(emptyForm)
      setKeepApiKey(false)
    }
  }, [open, configuracion])

  const setField = <K extends keyof ConfiguracionFidelizacionCreateInput>(
    key: K,
    value: ConfiguracionFidelizacionCreateInput[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }))

  const onNombreChange = (val: string) => {
    setField('nombre', val)
    if (!slugTouched && !isEditing) {
      setField('slug', toSlug(val))
    }
  }

  const onSlugChange = (val: string) => {
    setSlugTouched(true)
    setField('slug', toSlug(val))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)
    try {
      if (isEditing && configuracion) {
        const payload: Record<string, unknown> = {
          nombre: form.nombre,
          descripcion: form.descripcion,
          grupo_id: form.grupo_id,
          api_base_url: form.api_base_url,
          activo: form.activo,
          color_nombre: form.color_nombre,
          color_tono: form.color_tono,
        }
        if (!keepApiKey && form.api_key) payload.api_key = form.api_key
        await updateMut.mutateAsync({ id: configuracion.id, payload })
      } else {
        await createMut.mutateAsync(form)
      }
      onOpenChange(false)
    } catch (err) {
      const ui = parseFidelizacionError(err)
      setErrorMsg(ui.message)
    }
  }

  const isPending = createMut.isPending || updateMut.isPending
  const canSubmit =
    form.nombre.trim().length > 0 &&
    form.slug.trim().length > 0 &&
    form.grupo_id > 0 &&
    form.api_base_url.trim().length > 0 &&
    (isEditing ? (keepApiKey || form.api_key.length >= 8) : form.api_key.length >= 8)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            {isEditing ? 'Editar configuración' : 'Nueva configuración'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-2">
          {errorMsg && (
            <Alert variant="destructive">
              <AlertDescription>{errorMsg}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="grupo">Laboratorio *</Label>
            <Select
              value={form.grupo_id ? form.grupo_id.toString() : ''}
              onValueChange={(v) => setField('grupo_id', parseInt(v, 10))}
              disabled={grupos.isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un laboratorio" />
              </SelectTrigger>
              <SelectContent>
                {grupos.data?.map((g) => (
                  <SelectItem key={g.id} value={g.id.toString()}>
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="nombre">Nombre *</Label>
            <Input
              id="nombre"
              value={form.nombre}
              onChange={(e) => onNombreChange(e.target.value)}
              placeholder="Ej: Grupo Farma - Métricas mensuales"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">Slug *</Label>
            <Input
              id="slug"
              value={form.slug}
              onChange={(e) => onSlugChange(e.target.value)}
              placeholder="grupo-farma-mensual"
              pattern="^[a-z0-9][a-z0-9-]*$"
              required
              disabled={isEditing}
            />
            <p className="text-xs text-muted-foreground">Solo minúsculas, números y guiones. Único por laboratorio.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripción</Label>
            <Input
              id="descripcion"
              value={form.descripcion ?? ''}
              onChange={(e) => setField('descripcion', e.target.value)}
              placeholder="Opcional"
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="api_base_url">URL base del API *</Label>
            <Input
              id="api_base_url"
              type="url"
              value={form.api_base_url}
              onChange={(e) => setField('api_base_url', e.target.value)}
              placeholder="https://api.grupofarma.alteonx.com/v1/external/"
              required
            />
            <p className="text-xs text-muted-foreground">Debe ser HTTPS (o HTTP en localhost para stub) y contener <code>/v1/external</code>.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="api_key">API key {isEditing ? '(opcional al editar)' : '*'}</Label>
            {isEditing && (
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <p className="text-sm">Mantener la key actual</p>
                  <p className="text-xs text-muted-foreground">
                    {('api_key_masked' in (configuracion ?? {}) && configuracion?.api_key_masked) || 'sin key'}
                  </p>
                </div>
                <Switch checked={keepApiKey} onCheckedChange={setKeepApiKey} />
              </div>
            )}
            <Input
              id="api_key"
              type="password"
              value={form.api_key}
              onChange={(e) => setField('api_key', e.target.value)}
              placeholder="ak_ext_..."
              disabled={isEditing && keepApiKey}
              required={!isEditing}
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">Se cifra en reposo. Debe empezar por <code>ak_ext_</code>.</p>
          </div>

          <Separator />

          {/* Color de marca del reporte */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Color de marca</Label>
              <span className="text-xs text-muted-foreground capitalize">
                {form.color_nombre}-{form.color_tono}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Define la identidad visual del reporte (KPIs, charts, badges).
            </p>

            {/* Familias de color */}
            <div className="flex flex-wrap gap-1.5">
              {COLORES_DISPONIBLES.map((family) => {
                const isActive = form.color_nombre === family
                return (
                  <button
                    key={family}
                    type="button"
                    disabled={isPending}
                    onClick={() => setField('color_nombre', family)}
                    className={`h-7 w-7 rounded-md border-2 transition-all flex items-center justify-center ${
                      isActive ? 'border-foreground scale-110' : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: colorHex(family, '500') }}
                    title={family}
                  >
                    {isActive && (
                      <Check className="h-3.5 w-3.5 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]" />
                    )}
                  </button>
                )
              })}
            </div>

            {/* Variaciones del color seleccionado */}
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground capitalize">
                {form.color_nombre} — variaciones
              </p>
              <div className="flex gap-1.5">
                {TONOS_DISPONIBLES.map((tono) => {
                  const isActive = form.color_tono === tono
                  return (
                    <button
                      key={tono}
                      type="button"
                      disabled={isPending}
                      onClick={() => setField('color_tono', tono)}
                      className="flex flex-col items-center gap-0.5"
                    >
                      <div
                        className={`h-8 w-10 rounded-md border-2 transition-all flex items-center justify-center ${
                          isActive ? 'border-foreground scale-105' : 'border-transparent hover:scale-105'
                        }`}
                        style={{ backgroundColor: colorHex(form.color_nombre || 'emerald', tono) }}
                      >
                        {isActive && (
                          <Check className="h-3.5 w-3.5 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]" />
                        )}
                      </div>
                      <span className="text-[10px] text-muted-foreground">{tono}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="activo">Configuración activa</Label>
              <p className="text-sm text-muted-foreground">
                Si está apagada, el reporte no se podrá consultar.
              </p>
            </div>
            <Switch
              id="activo"
              checked={form.activo ?? true}
              onCheckedChange={(checked) => setField('activo', checked)}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isPending || !canSubmit}
              className="bg-blue-800 hover:bg-blue-900 text-white"
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Guardar cambios' : 'Crear configuración'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
